import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { User, Session, AuthError } from "@supabase/supabase-js";
import supabase from "@/lib/supabase";
import { userSyncApi, type LocalUser } from "@/services/userSyncApi";
import { PermissionManager } from "@/lib/permissions";

interface AuthState {
  // Auth state
  user: User | null;
  localUser: LocalUser | null;
  permissions: string[] | null;
  session: Session | null;
  loading: boolean;
  initialized: boolean;
  error: string | null;
  signingOut: boolean;
  clearingAuth: boolean; // Flag to prevent redirect loops during sign out

  // Actions
  signIn: (
    email: string,
    password: string
  ) => Promise<{ error: AuthError | null }>;
  signUp: (
    email: string,
    password: string,
    firstName: string,
    lastName: string
  ) => Promise<{ error: AuthError | null }>;
  signInWithGoogle: () => Promise<{ error: AuthError | null }>;
  signInWithGoogleButton: (
    response: any
  ) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  clearError: () => void;
  clearStore: () => void;

  // Permission checking methods
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  checkPermissionRule: (rule: {
    anyOf?: string[];
    allOf?: string[];
    not?: string[];
  }) => boolean;

  // Internal state management
  setUser: (user: User | null) => void;
  setLocalUser: (localUser: LocalUser | null) => void;
  setSession: (session: Session | null) => void;
  setLoading: (loading: boolean) => void;
  setInitialized: (initialized: boolean) => void;
  setError: (error: string | null) => void;
  syncUser: (firstName?: string, lastName?: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      localUser: null,
      permissions: null,
      session: null,
      loading: false,
      initialized: false,
      error: null,
      signingOut: false,
      clearingAuth: false,

      // Permission checking methods
      hasPermission: (permission: string) => {
        const { permissions } = get();
        if (!permissions) return false;
        return permissions.includes(permission);
      },

      hasAnyPermission: (permissionsToCheck: string[]) => {
        const { permissions } = get();
        if (!permissions) return false;
        return permissions.some((p) => permissionsToCheck.includes(p));
      },

      hasAllPermissions: (permissionsToCheck: string[]) => {
        const { permissions } = get();
        if (!permissions) return false;
        return permissions.every((p) => permissionsToCheck.includes(p));
      },

      checkPermissionRule: (rule: {
        anyOf?: string[];
        allOf?: string[];
        not?: string[];
      }) => {
        const { hasPermission, hasAnyPermission, hasAllPermissions } = get();

        // Check 'not' conditions
        if (rule.not && rule.not.length > 0) {
          for (const perm of rule.not) {
            if (hasPermission(perm)) return false;
          }
        }

        // Check 'allOf' conditions
        if (rule.allOf && rule.allOf.length > 0) {
          if (!hasAllPermissions(rule.allOf)) return false;
        }

        // Check 'anyOf' conditions
        if (rule.anyOf && rule.anyOf.length > 0) {
          if (!hasAnyPermission(rule.anyOf)) return false;
        }

        return true;
      },

      // Auth actions
      signIn: async (email: string, password: string) => {
        set({ loading: true, error: null });

        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) {
          set({ loading: false, error: error.message });
          return { error };
        }

        set({
          user: data.user,
          session: data.session,
          loading: false,
          error: null,
        });

        // User sync will be handled by onAuthStateChange listener
        // This makes login faster by not blocking on the sync call

        return { error: null };
      },

      signUp: async (
        email: string,
        password: string,
        firstName: string,
        lastName: string
      ) => {
        set({ loading: true, error: null });

        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              first_name: firstName,
              last_name: lastName,
            },
          },
        });

        if (error) {
          set({ loading: false, error: error.message });
          return { error };
        }

        set({
          user: data.user,
          session: data.session,
          loading: false,
          error: null,
        });

        return { error: null };
      },

      signInWithGoogle: async () => {
        set({ loading: true, error: null });

        const { data, error } = await supabase.auth.signInWithOAuth({
          provider: "google",
          options: {
            redirectTo: `${window.location.origin}/google-login`,
          },
        });
        console.log("Google sign in data", data);

        if (error) {
          set({ loading: false, error: error.message });
          return { error };
        }

        // OAuth flow will handle the redirect, so we don't set user/session here
        set({ loading: false });
        return { error: null };
      },

      // Sign in with Google using their button
      signInWithGoogleButton: async (response: any) => {
        set({ loading: true, error: null });

        const { error, data } = await supabase.auth.signInWithIdToken({
          provider: "google",
          token: response.credential,
        });

        if (error) {
          console.error("Error signing in with Google:", error);
          set({ loading: false, error: error.message });
          return { error };
        }
        // console.log("Successfully signed in with Google:", data);

        set({ loading: false });
        return { error: null, data };
      },

      signOut: async () => {
        set({ loading: true, signingOut: true, clearingAuth: true });

        try {
          await supabase.auth.signOut();
        } catch (error) {
          console.error("Error signing out:", error);
        } finally {
          // Clear static permission cache
          PermissionManager.clearCache();

          set({
            user: null,
            localUser: null,
            permissions: null,
            session: null,
            loading: false,
            error: null,
            signingOut: false,
            clearingAuth: false,
          });
        }
      },

      clearError: () => set({ error: null }),
      clearStore: () =>
        set({
          user: null,
          localUser: null,
          permissions: null,
          session: null,
          loading: false,
          error: null,
          signingOut: false,
          clearingAuth: false,
        }),

      // Internal state management
      setUser: (user) => set({ user }),
      setLocalUser: (localUser) => {
        const currentUser = get().localUser;
        // Clear permission cache when role changes
        if (currentUser?.role !== localUser?.role) {
          // Clear static permission cache
          PermissionManager.clearCache();
        }
        set({
          localUser,
          permissions: localUser?.permissions || null,
        });
      },
      setSession: (session) => set({ session }),
      setLoading: (loading) => set({ loading }),
      setInitialized: (initialized) => set({ initialized }),
      setError: (error) => set({ error }),

      // User sync method
      syncUser: async (firstName?: string, lastName?: string) => {
        try {
          // User sync API now returns permissions with the user data
          const localUser = await userSyncApi.syncUser({ firstName, lastName });
          set({
            localUser,
            permissions: localUser?.permissions || [],
          });
        } catch (error) {
          if (error instanceof Error) {
            // Handle different error types
            if (error.message === "Authentication required") {
              console.log("Authentication required, signing out");
              get().signOut();
            } else if (
              error.message.includes("Service temporarily unavailable") ||
              error.message.includes("Database connection error")
            ) {
              // Database connection issues - don't sign out, just log
              console.warn(
                "Database temporarily unavailable during user sync:",
                error.message
              );
              // User can continue with cached data or retry later
            } else {
              console.error("Failed to sync user:", error.message);
              // Don't sign out for other errors, just log them
            }
          } else {
            console.error("Unexpected error during user sync:", error);
          }
        }
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        user: state.user,
        localUser: state.localUser,
        permissions: state.permissions,
        session: state.session,
        initialized: state.initialized,
      }),
    }
  )
);

// Track if auth has been initialized to prevent multiple calls
let authInitialized = false;
let syncTimeout: NodeJS.Timeout | null = null;

// Debounced user sync to prevent duplicate calls
const debouncedSyncUser = (
  syncUser: () => Promise<void>,
  delay: number = 500
) => {
  if (syncTimeout) {
    clearTimeout(syncTimeout);
  }

  syncTimeout = setTimeout(async () => {
    try {
      await syncUser();
      syncTimeout = null;
    } catch (error) {
      console.warn("Failed to sync user:", error);
      syncTimeout = null;
    }
  }, delay);
};

// Initialize auth state listener
export const initializeAuth = () => {
  // Prevent multiple initializations
  if (authInitialized) {
    return;
  }

  authInitialized = true;

  const { clearStore, setUser, setSession, setInitialized, syncUser } =
    useAuthStore.getState();

  // Get initial session - trust persisted session initially
  supabase.auth.getSession().then(async ({ data: { session } }) => {
    if (session?.user) {
      // Set session immediately without validation for faster startup
      setUser(session.user);
      setSession(session);

      // Sync user in background (debounced)
      debouncedSyncUser(syncUser, 100);
    }

    setInitialized(true);
  });

  // Listen for auth state changes
  supabase.auth.onAuthStateChange(async (event, session) => {
    console.log("Auth state changed:", event);

    setUser(session?.user ?? null);
    setSession(session);

    // Handle different auth events
    if (event === "SIGNED_IN") {
      // User just signed in, sync immediately but debounced
      debouncedSyncUser(syncUser, 100);
    } else if (event === "TOKEN_REFRESHED") {
      // Token was refreshed, sync user data (debounced to avoid spam)
      debouncedSyncUser(syncUser, 1000);
    } else if (event === "SIGNED_OUT") {
      // Clear permission cache and sync timeout when user signs out
      if (syncTimeout) {
        clearTimeout(syncTimeout);
        syncTimeout = null;
      }
      PermissionManager.clearCache();
      clearStore();
    } else if (event === "USER_UPDATED") {
      // User metadata updated, sync
      debouncedSyncUser(syncUser, 500);
    }

    if (!useAuthStore.getState().initialized) {
      setInitialized(true);
    }
  });
};
